import React, { useCallback, useEffect, useMemo, useState } from 'react';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  IconButton,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import {
  fetchTutorImport,
  fetchTutorImports,
  downloadTutorImportFile,
  sendTutorImportVerificationLink,
  updateTutorImportReview,
  uploadTutorImportCsv,
} from '../services/tutorImports';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'published', label: 'Published' },
  { value: 'validated', label: 'Validated' },
  { value: 'failed', label: 'Failed' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const APEX_BRAND_BLUE = '#00154A';

const combinedStatus = (item = {}) =>
  item.combinedStatus || {
    label: item.status || 'Unknown',
    tone: item.status || 'warning',
  };

const statusColor = (valueOrTone) => {
  switch (String(valueOrTone || '').toLowerCase()) {
    case 'success':
    case 'published':
    case 'validated':
    case 'approved':
      return 'success';
    case 'error':
    case 'failed':
    case 'rejected':
      return 'error';
    case 'info':
      return 'info';
    case 'neutral':
      return 'default';
    default:
      return 'warning';
  }
};

const statusChipSx = (valueOrTone) => (
  String(valueOrTone || '').toLowerCase() === 'success'
    ? {
        bgcolor: APEX_BRAND_BLUE,
        color: '#fff',
        fontWeight: 700,
        '& .MuiChip-label': {
          color: '#fff',
        },
      }
    : undefined
);

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};

const formatList = (value) => (Array.isArray(value) && value.length ? value.join(', ') : '-');
const verificationLabel = (item = {}) => {
  if (item.emailVerified) return 'Email verified';
  if (item.emailVerificationSentAt) return 'Verification sent';
  return 'Email unverified';
};
const verificationColor = (item = {}) => {
  if (item.emailVerified) return 'success';
  if (item.emailVerificationSentAt) return 'info';
  return 'warning';
};
const getDegreesForDisplay = (normalizedData = {}) => {
  const fromNormalized = Array.isArray(normalizedData.degrees) ? normalizedData.degrees : [];
  if (fromNormalized.length) return fromNormalized;
  if (normalizedData.degreeType || normalizedData.degreeSpecialization || normalizedData.institutionName || normalizedData.graduationYear) {
    return [
      {
        degreeType: normalizedData.degreeType || '-',
        degreeSpecialization: normalizedData.degreeSpecialization || '-',
        institutionName: normalizedData.institutionName || '-',
        graduationYear: normalizedData.graduationYear || '-',
      },
    ];
  }
  return [];
};

const getDegreeDocumentsForDisplay = (applicationFiles = {}, degreeIndex) => {
  const byIndex = applicationFiles?.degreeDocuments;
  if (!byIndex || typeof byIndex !== 'object') return [];
  const key = String(degreeIndex);
  return Array.isArray(byIndex[key]) ? byIndex[key] : [];
};

const getFileName = (file) => file?.filename || (file?.url ? String(file.url).split('/').pop() : '');

const documentSummaryLabel = (summary = {}) => {
  const count = Number(summary?.documentCount || 0);
  return count === 1 ? '1 document' : `${count} documents`;
};

export default function TutorImports() {
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [sourceName, setSourceName] = useState('manual_csv');
  const [file, setFile] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [reviewDialog, setReviewDialog] = useState({ open: false, item: null });
  const [helpOpen, setHelpOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState('approved');
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verificationSubmittingId, setVerificationSubmittingId] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchTutorImports({
        status: status || undefined,
        q: query || undefined,
        includeArchived: includeArchived || undefined,
        limit: 100,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setSummary(data?.summary || {});
    } catch (err) {
      setItems([]);
      setSummary({});
      setError(err.message || 'Failed to load tutor imports');
    } finally {
      setLoading(false);
    }
  }, [includeArchived, query, status]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const counts = useMemo(
    () => ({
      validated: summary.validated || 0,
      published: summary.published || 0,
      failed: summary.failed || 0,
      approved: summary.approved || 0,
      rejected: summary.rejected || 0,
    }),
    [summary]
  );

  const detailDegrees = useMemo(() => getDegreesForDisplay(detailItem?.normalizedData || {}), [detailItem]);

  const handleUpload = async () => {
    if (!file) {
      setError('Choose a CSV file first.');
      return;
    }
    try {
      setUploading(true);
      setError('');
      await uploadTutorImportCsv({ file, sourceName });
      setFile(null);
      await loadItems();
    } catch (err) {
      setError(err.message || 'Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  const openDetail = async (item) => {
    try {
      setDetailOpen(true);
      setDetailLoading(true);
      setError('');
      const data = await fetchTutorImport(item._id);
      setDetailItem(data);
    } catch (err) {
      setError(err.message || 'Failed to load import detail');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const openReviewDialog = (item, nextStatus) => {
    setReviewDialog({ open: true, item });
    setReviewStatus(nextStatus);
    setReviewNotes(item?.reviewNotes || '');
  };

  const submitReview = async () => {
    if (!reviewDialog.item?._id) return;
    if (reviewStatus === 'rejected' && !reviewNotes.trim()) {
      setError('A rejection note is required before rejecting a tutor application.');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      const result = await updateTutorImportReview(reviewDialog.item._id, {
        status: reviewStatus,
        reviewNotes,
      });
      if (detailItem && String(detailItem._id) === String(reviewDialog.item._id)) {
        setDetailItem(result?.item || detailItem);
      }
      setReviewDialog({ open: false, item: null });
      await loadItems();
    } catch (err) {
      setError(err.message || 'Failed to update tutor import');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendVerificationLink = async (item, options = {}) => {
    if (!item?._id) return;
    try {
      setVerificationSubmittingId(item._id);
      setError('');
      const result = await sendTutorImportVerificationLink(item._id, options);
      if (detailItem && String(detailItem._id) === String(item._id)) {
        setDetailItem(result?.item || detailItem);
      }
      await loadItems();
    } catch (err) {
      setError(err.message || 'Failed to send email verification link');
    } finally {
      setVerificationSubmittingId('');
    }
  };

  const openTutorDocument = async (file) => {
    const filename = getFileName(file);
    if (!detailItem?._id || !filename) return;
    try {
      setError('');
      const blob = await downloadTutorImportFile(detailItem._id, filename);
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (err) {
      setError(err.message || 'Failed to open tutor document');
    }
  };

  const renderDocumentButton = (file, fallbackLabel) => (
    <Button
      onClick={() => openTutorDocument(file)}
      size="small"
      variant="outlined"
      disabled={!getFileName(file)}
    >
      {file?.originalName || fallbackLabel}
    </Button>
  );

  const webhookExample = `curl -X POST https://api.apexacademiccentre.co.za/api/tutor-imports/webhook \\
  -H "Content-Type: application/json" \\
  -H "x-tutor-import-secret: YOUR_SECRET" \\
  -H "x-tutor-import-source: cv_manager" \\
  -d '{
    "items": [
      {
        "externalId": "TUT-123",
        "name": "Jane",
        "surname": "Doe",
        "email": "jane.doe@example.com",
        "phone": "0712345678",
        "province": "Gauteng",
        "town": "Johannesburg",
        "suburb": "Sandton",
        "curricula": "CAPS,IEB",
        "gradeLevels": "8,9,10,11,12",
        "subjects": "English,Mathematics",
        "tutoringOptions": "online,in-person",
        "program": "general",
        "portals": "tutor"
      }
    ]
  }'`;

  const webhookJsonExample = `{
  "items": [
    {
      "externalId": "TUT-123",
      "name": "Jane",
      "surname": "Doe",
      "email": "jane.doe@example.com",
      "phone": "0712345678",
      "province": "Gauteng",
      "town": "Johannesburg",
      "suburb": "Sandton",
      "curricula": "CAPS,IEB",
      "gradeLevels": "8,9,10,11,12",
      "subjects": "English,Mathematics",
      "tutoringOptions": "online,in-person",
      "program": "general",
      "portals": "tutor"
    }
  ]
}`;

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Card>
          <CardHeader
            action={(
              <Tooltip title="Webhook import help">
                <IconButton size="small" onClick={() => setHelpOpen(true)} aria-label="Tutor import help">
                  <HelpOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          />
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Button component="label" variant="outlined" startIcon={<CloudUploadRoundedIcon />}>
                Choose CSV
                <input hidden type="file" accept=".csv,text/csv" onChange={(event) => setFile(event.target.files?.[0] || null)} />
              </Button>
              <Typography variant="body2" color={file ? 'text.primary' : 'text.secondary'} sx={{ minWidth: { md: 160 }, overflowWrap: 'anywhere' }}>
                {file?.name || 'No file selected'}
              </Typography>
              <TextField
                label="Source Name"
                value={sourceName}
                onChange={(event) => setSourceName(event.target.value)}
                sx={{ minWidth: { md: 220 } }}
              />
              <Button variant="contained" onClick={handleUpload} disabled={uploading}>
                Upload CSV
              </Button>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 3 }}>
              <FormControl fullWidth sx={{ minWidth: { md: 220 } }}>
                <InputLabel id="tutor-import-status-label">Status</InputLabel>
                <Select
                  labelId="tutor-import-status-label"
                  value={status}
                  label="Status"
                  onChange={(event) => setStatus(event.target.value)}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Email, subject, source, external id"
                onKeyDown={(event) => {
                  if (event.key === 'Enter') loadItems();
                }}
              />
              <Button variant="outlined" onClick={loadItems} disabled={loading}>
                Refresh
              </Button>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeArchived}
                    onChange={(event) => setIncludeArchived(event.target.checked)}
                  />
                }
                label="Show archived"
                sx={{ minWidth: { md: 180 } }}
              />
            </Stack>

            {uploading || loading ? <LinearProgress sx={{ mt: 2 }} /> : null}
            {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 3 }}>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Published</Typography>
                  <Typography variant="h5" fontWeight={800}>{counts.published}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Validated</Typography>
                  <Typography variant="h5" fontWeight={800}>{counts.validated}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Failed</Typography>
                  <Typography variant="h5" fontWeight={800}>{counts.failed}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Approved</Typography>
                  <Typography variant="h5" fontWeight={800}>{counts.approved}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Rejected</Typography>
                  <Typography variant="h5" fontWeight={800}>{counts.rejected}</Typography>
                </CardContent>
              </Card>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title="Staged Tutor Imports"
            subheader={`${items.length} import${items.length === 1 ? '' : 's'} loaded`}
          />
          <Divider />
          <CardContent sx={{ p: 0 }}>
            <TableContainer className="admin-scroll-table">
              <Table size="small">
                <TableHead>
	                  <TableRow>
	                    <TableCell>Source</TableCell>
	                    <TableCell>Name</TableCell>
	                    <TableCell>Email</TableCell>
	                    <TableCell>Email Verification</TableCell>
	                    <TableCell>Status</TableCell>
	                    <TableCell>Matched User</TableCell>
	                    <TableCell>Documents</TableCell>
	                    <TableCell>Errors</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && items.length === 0 ? (
                    <TableRow>
	                      <TableCell colSpan={10}>
                        <Box sx={{ py: 5, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            No tutor imports match the current filters.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {items.map((item) => (
                    <TableRow key={item._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{item.sourceName}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.sourceType}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.normalizedData?.name} {item.normalizedData?.surname}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatList(item.normalizedData?.subjects)}</Typography>
                      </TableCell>
	                      <TableCell>{item.normalizedData?.email || '-'}</TableCell>
	                      <TableCell>
	                        <Stack spacing={0.5}>
	                          <Chip size="small" label={verificationLabel(item)} color={verificationColor(item)} />
	                          {item.emailVerificationSentAt && !item.emailVerified ? (
	                            <Typography variant="caption" color="text.secondary">
	                              Sent {formatDate(item.emailVerificationSentAt)}
	                            </Typography>
	                          ) : null}
	                        </Stack>
	                      </TableCell>
                      <TableCell>
                        {(() => {
                          const state = combinedStatus(item);
                          return (
                            <Stack spacing={0.5} alignItems="flex-start">
                              <Chip
                                size="small"
                                label={state.label}
                                color={statusColor(state.tone)}
                                sx={statusChipSx(state.tone)}
                              />
                              {item.archiveReason ? (
                                <Typography variant="caption" color="text.secondary">
                                  {item.archiveReason}
                                </Typography>
                              ) : null}
                            </Stack>
                          );
                        })()}
                      </TableCell>
                      {/* <TableCell>
                        <Chip
                          size="small"
                          label={item.status}
                          color={statusColor(item.status)}
                          sx={statusChipSx(item.status)}
                        />
                      </TableCell> */}
	                      <TableCell>
	                        {item.matchedUser?.email ? (
                          <>
                            <Typography variant="body2">{item.matchedUser.email}</Typography>
                            <Typography variant="caption" color="text.secondary">{item.matchedUser.role} · {item.matchedUser.approvalStatus || 'approved'}</Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
	                      </TableCell>
	                      <TableCell>
	                        <Typography variant="body2">
	                          {documentSummaryLabel(item.normalizedData?.documentSummary)}
	                        </Typography>
	                        {item.normalizedData?.documentSummary?.degreeDocumentCount ? (
	                          <Typography variant="caption" color="text.secondary">
	                            {item.normalizedData.documentSummary.degreeDocumentCount} degree document{item.normalizedData.documentSummary.degreeDocumentCount === 1 ? '' : 's'}
	                          </Typography>
	                        ) : null}
	                      </TableCell>
                      <TableCell>{Array.isArray(item.validationErrors) ? item.validationErrors.length : 0}</TableCell>
                      <TableCell>{formatDate(item.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
	                          <Button size="small" onClick={() => openDetail(item)}>View</Button>
	                          {item.sourceType === 'public_form' && !item.emailVerified ? (
	                            <Button
	                              size="small"
	                              variant="outlined"
	                              onClick={() => handleSendVerificationLink(item)}
	                              disabled={verificationSubmittingId === item._id}
	                            >
	                              Send Verify Link
	                            </Button>
	                          ) : null}
	                          <Button size="small" variant="contained" color="success" onClick={() => openReviewDialog(item, 'approved')}>
                            Approve
                          </Button>
                          <Button size="small" variant="outlined" color="error" onClick={() => openReviewDialog(item, 'rejected')}>
                            Reject
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Stack>

      <Drawer anchor="right" open={detailOpen} onClose={() => setDetailOpen(false)}>
        <Box sx={{ width: { xs: '100vw', sm: 460 }, p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={800}>Tutor Import Detail</Typography>
            {detailLoading ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : detailItem ? (
              <>
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">Source</Typography>
                      <Typography variant="body1" fontWeight={700}>
                        {detailItem.sourceName} · {detailItem.sourceType}
                      </Typography>
	                      <Stack direction="row" spacing={1} flexWrap="wrap">
	                        {(() => {
	                          const state = combinedStatus(detailItem);
	                          return (
	                            <Chip
	                              size="small"
	                              label={state.label}
	                              color={statusColor(state.tone)}
	                              sx={statusChipSx(state.tone)}
	                            />
	                          );
	                        })()}
	                        <Chip size="small" label={verificationLabel(detailItem)} color={verificationColor(detailItem)} />
	                      </Stack>
	                      {detailItem.archiveReason ? (
	                        <Typography variant="caption" color="text.secondary">
	                          Archived: {detailItem.archiveReason}
	                        </Typography>
	                      ) : null}
	                      {detailItem.emailVerificationSentAt ? (
	                        <Typography variant="caption" color="text.secondary">
	                          Verification sent: {formatDate(detailItem.emailVerificationSentAt)}
	                          {detailItem.emailVerificationTokenExpires ? ` · expires ${formatDate(detailItem.emailVerificationTokenExpires)}` : ''}
	                        </Typography>
	                      ) : null}
	                      {detailItem.emailVerifiedAt ? (
	                        <Typography variant="caption" color="text.secondary">
	                          Verified: {formatDate(detailItem.emailVerifiedAt)}
	                          {detailItem.emailVerificationMethod ? ` · ${detailItem.emailVerificationMethod}` : ''}
	                        </Typography>
	                      ) : null}
	                      {detailItem.sourceType === 'public_form' && !detailItem.emailVerified ? (
	                        <Button
	                          size="small"
	                          variant="outlined"
	                          onClick={() => handleSendVerificationLink(detailItem)}
	                          disabled={verificationSubmittingId === detailItem._id}
	                          sx={{ alignSelf: 'flex-start', mt: 1 }}
	                        >
	                          Send Email Verification Link
	                        </Button>
	                      ) : null}
	                    </Stack>
	                  </CardContent>
	                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Normalized Data</Typography>
                      <Typography variant="body2">Name: {detailItem.normalizedData?.name} {detailItem.normalizedData?.surname}</Typography>
                      <Typography variant="body2">ID / Passport / Driver License Number: {detailItem.normalizedData?.identityNumber || '-'}</Typography>
                      <Typography variant="body2">Email: {detailItem.normalizedData?.email || '-'}</Typography>
                      <Typography variant="body2">Date of Birth: {detailItem.normalizedData?.dateOfBirth || '-'}</Typography>
                      <Typography variant="body2">Gender: {detailItem.normalizedData?.gender || '-'}</Typography>
                      <Typography variant="body2">Phone: {detailItem.normalizedData?.phone || '-'}</Typography>
                      <Typography variant="body2">Country: {detailItem.normalizedData?.country || '-'}</Typography>
                      <Typography variant="body2">Province: {detailItem.normalizedData?.province || '-'}</Typography>
                      <Typography variant="body2">Postal Code: {detailItem.normalizedData?.postalCode || '-'}</Typography>
                      <Typography variant="body2">Town/Suburb: {detailItem.normalizedData?.town || '-'} / {detailItem.normalizedData?.suburb || '-'}</Typography>
                      <Typography variant="body2">Address: {detailItem.normalizedData?.addressLine1 || '-'}</Typography>
                      <Typography variant="body2">Curricula: {formatList(detailItem.normalizedData?.curricula)}</Typography>
                      <Typography variant="body2">Grade Levels: {formatList(detailItem.normalizedData?.gradeLevels)}</Typography>
                      <Typography variant="body2">Subjects: {formatList(detailItem.normalizedData?.subjects)}</Typography>
                      <Typography variant="body2">Tutoring Options: {formatList(detailItem.normalizedData?.tutoringOptions)}</Typography>
                      <Typography variant="body2">Bio: {detailItem.normalizedData?.bio || '-'}</Typography>
                      <Typography variant="body2">Degrees</Typography>
                      {detailDegrees.length ? (
                        <Stack spacing={1} sx={{ mt: 0.5 }}>
                          {detailDegrees.map((degree, index) => (
                            <Box key={`${degree.degreeType}-${degree.institutionName}-${index}`} sx={{ pl: 1 }}>
                              <Typography variant="caption" color="text.secondary">Degree {index + 1}</Typography>
                              <Typography variant="body2">
                                {degree.degreeType} · {degree.institutionName} · {degree.graduationYear}
                              </Typography>
                              <Typography variant="body2">Specialization: {degree.degreeSpecialization}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No degree details provided</Typography>
                      )}
                      <Typography variant="body2">Special Needs Support: {formatList(detailItem.normalizedData?.specialNeedsSupport)}</Typography>
                      <Typography variant="body2">Subject Experience: {Array.isArray(detailItem.normalizedData?.subjectExperience) && detailItem.normalizedData.subjectExperience.length ? detailItem.normalizedData.subjectExperience.map((entry) => `${entry.subject}: ${entry.years}`).join(', ') : '-'}</Typography>
                      <Typography variant="body2">Bank Name: {detailItem.normalizedData?.bankName || '-'}</Typography>
                      <Typography variant="body2">Bank Account Name: {detailItem.normalizedData?.bankAccountName || '-'}</Typography>
                      <Typography variant="body2">Bank Account Number: {detailItem.normalizedData?.bankAccountNumber || '-'}</Typography>
                      <Typography variant="body2">Program: {detailItem.normalizedData?.program || '-'}</Typography>
                      <Typography variant="body2">Portals: {formatList(detailItem.normalizedData?.portals)}</Typography>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Application Documents</Typography>
                      {detailItem.normalizedData?.applicationFiles?.cv?.url ? (
                        renderDocumentButton(detailItem.normalizedData.applicationFiles.cv, 'View CV')
                      ) : (
                        <Typography variant="body2" color="text.secondary">No CV uploaded</Typography>
                      )}
                      {detailItem.normalizedData?.applicationFiles?.profilePicture?.url ? (
                        renderDocumentButton(detailItem.normalizedData.applicationFiles.profilePicture, 'View Profile Picture')
                      ) : (
                        <Typography variant="body2" color="text.secondary">No profile picture uploaded</Typography>
                      )}
                      {detailItem.normalizedData?.applicationFiles?.identityDocument?.url ? (
                        renderDocumentButton(detailItem.normalizedData.applicationFiles.identityDocument, 'View ID / Passport / Driver License')
                      ) : (
                        <Typography variant="body2" color="text.secondary">No ID, passport, or driver license document uploaded</Typography>
                      )}
                      {Array.isArray(detailItem.normalizedData?.applicationFiles?.supportingDocuments) && detailItem.normalizedData.applicationFiles.supportingDocuments.length ? (
                        <Stack spacing={1}>
                          {detailItem.normalizedData.applicationFiles.supportingDocuments.map((file, index) => (
                            <React.Fragment key={`${file?.url || 'supporting'}-${index}`}>
                              {renderDocumentButton(file, `Supporting Document ${index + 1}`)}
                            </React.Fragment>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No supporting documents uploaded</Typography>
                      )}
                      {detailDegrees.map((degree, index) => {
                        const degreeDocuments = getDegreeDocumentsForDisplay(detailItem.normalizedData?.applicationFiles || {}, index);
                        return (
                          <Box key={`degree-docs-${index}`} sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Degree {index + 1} documents
                            </Typography>
                            {degreeDocuments.length ? (
                              <Stack spacing={1}>
                                {degreeDocuments.map((file, fileIndex) => (
                                  <React.Fragment key={`${file?.url || 'degree-doc'}-${index}-${fileIndex}`}>
                                    {renderDocumentButton(file, `Degree ${index + 1} Document ${fileIndex + 1}`)}
                                  </React.Fragment>
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No documents uploaded for this degree</Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Validation</Typography>
                      {(detailItem.validationErrors || []).length ? (
                        detailItem.validationErrors.map((entry, index) => (
                          <Typography key={`${entry}-${index}`} variant="body2" color="error">
                            {entry}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="success.main">No validation errors</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Matched Live User</Typography>
                      {detailItem.matchedUser?.email ? (
                        <>
                          <Typography variant="body2">{detailItem.matchedUser.email}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {detailItem.matchedUser.role} · {formatList(detailItem.matchedUser.portals)} · {detailItem.matchedUser.approvalStatus || 'approved'}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No live user match</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

              </>
            ) : null}
          </Stack>
        </Box>
      </Drawer>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Tutor Import Help</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>Import Modes</Typography>
              <Typography variant="body2" color="text.secondary">
                CSV uploads and webhook imports both stage tutor records first. Nothing is published into booking or LMS until an admin reviews and approves the import.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={800}>Webhook Endpoint</Typography>
              <Typography variant="body2">`POST /api/tutor-imports/webhook`</Typography>
              <Typography variant="body2" color="text.secondary">
                Required secret:
                `x-tutor-import-secret`
                or
                `Authorization: Bearer &lt;secret&gt;`
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Optional source header:
                `x-tutor-import-source`
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={800}>Supported Payload Shapes</Typography>
              <Typography variant="body2" color="text.secondary">
                The webhook accepts `items`, `tutors`, `records`, or a single object body.
              </Typography>
              <Box component="pre" sx={{ m: 0, mt: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                {webhookJsonExample}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={800}>Example Curl</Typography>
              <Box component="pre" sx={{ m: 0, mt: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                {webhookExample}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={800}>Source-Specific Mapping</Typography>
              <Typography variant="body2" color="text.secondary">
                The backend defaults to direct field mapping. A `cv_manager` source mapper is included for common aliases such as `first_name`, `last_name`, `subjects_taught`, `delivery_modes`, and `portal_access`.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={800}>Audit Logging</Typography>
              <Typography variant="body2" color="text.secondary">
                Webhook requests are logged with request id, source name, IP, import batch id, row counts, created vs updated counts, validation outcomes, and publish events. Invalid secrets and empty payloads are also logged for traceability.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reviewDialog.open} onClose={() => setReviewDialog({ open: false, item: null })} fullWidth maxWidth="sm">
        <DialogTitle>Review Tutor Import</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {reviewDialog.item?.normalizedData?.email || '-'} · {reviewDialog.item?.normalizedData?.name || '-'} {reviewDialog.item?.normalizedData?.surname || ''}
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="review-status-label">Status</InputLabel>
              <Select
                labelId="review-status-label"
                value={reviewStatus}
                label="Status"
                onChange={(event) => setReviewStatus(event.target.value)}
              >
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="validated">Validated</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <TextField
              multiline
              minRows={3}
              fullWidth
              label={reviewStatus === 'rejected' ? 'Rejection Note Required' : 'Review Notes'}
              value={reviewNotes}
              onChange={(event) => setReviewNotes(event.target.value)}
              placeholder={reviewStatus === 'rejected' ? 'Enter the note that will be included in the rejection email' : 'Leave an approval or review note'}
              required={reviewStatus === 'rejected'}
              error={reviewStatus === 'rejected' && !reviewNotes.trim()}
              helperText={reviewStatus === 'rejected' ? 'This note is sent to the tutor in the rejection email.' : ''}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog({ open: false, item: null })} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={submitReview} disabled={submitting || (reviewStatus === 'rejected' && !reviewNotes.trim())}>
            Save Review
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
